import { useCallback, useMemo, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import {
  GET_USER_GROUPS_DEFAULT_PARAMS,
  useDeleteUserGroupAPI,
  useGetUserGroupsAPI,
  usePostUserGroupAPI,
  usePutUserGroupAPI
} from "@/api/userGroups/useUserGroupsAPI";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import { columns } from "@/components/groupManagement/columns";
import {
  GROUP_MANAGEMENT_BREADCRUMB_CONFIG,
  GROUP_MANAGEMENT_NO_DATA_MESSAGE_MAPPING,
  GroupStatusOptions
} from "@/components/groupManagement/data";
import GroupPanel from "@/components/groupManagement/GroupPanel";
import { useGroupManagementToast } from "@/components/groupManagement/hooks";
import GroupManagementHeader from "@/components/ui/DashboardHeader";
import GroupManagementGroupList from "@/components/ui/DashboardList";
import { NoDataMessage } from "@/components/ui/NoDataMessage";
import { MESSAGE } from "@/constants/message";
import {
  EModuleName,
  EModulePermission,
  useGetUserPermission
} from "@/hooks/permission/useGetUserPermission";
import { useConfirmationModal } from "@/hooks/ui/modal/useConfirmationModal";
import { EToastType } from "@/hooks/ui/useToast";
import { useDebouncedCallback } from "@/hooks/useDebounce";
import {
  AddGroupFormData,
  DeleteGroupPayload,
  EditGroupFormData,
  GetGroupsPayload,
  GetUserGroupsData,
  GroupPanelMode
} from "@/types/groups";

const TEST_ID_PREFIX = "group-management-page";
const DEFAULT_GET_GROUPS_PER_PAGE = 10;

const GroupManagementPage = () => {
  const { showGroupManagementToast } = useGroupManagementToast();
  const queryClient = useQueryClient();

  const { showConfirmation } = useConfirmationModal();

  const { data: userProfileData } = useGetUserProfileAPI();

  const [searchParams, setSearchParams] = useState<GetGroupsPayload>({
    ...GET_USER_GROUPS_DEFAULT_PARAMS,
    per_page: DEFAULT_GET_GROUPS_PER_PAGE
  });

  const [groupPanelMode, setGroupPanelMode] = useState<GroupPanelMode | null>(
    null
  );
  const [selectedGroup, setSelectedGroup] = useState<GetUserGroupsData | null>(
    null
  );

  const debouncedSetSearchParams = useDebouncedCallback(
    (newValue: Partial<GetGroupsPayload>) => {
      setSearchParams((prevValue) => ({ ...prevValue, ...newValue }));
    },
    500
  );
  const { data: userGroupsData, isLoading: isUserGroupsLoading } =
    useGetUserGroupsAPI({
      query: searchParams.query,
      status: searchParams.status,
      sort_by: searchParams.sort_by,
      order_by: searchParams.order_by,
      page: searchParams.page,
      per_page: searchParams.per_page
    });

  const invalidateQueryAfterAction = useCallback(
    (response: { id: string } = { id: "" }) => {
      if (response?.id) {
        queryClient.invalidateQueries({
          queryKey: ["getUserGroupById", response.id] // Invalidate the specific user group by ID query
        });
      }
      queryClient.invalidateQueries({
        queryKey: ["getUserGroups"] // Invalidate all user groups queries
      });
      queryClient.invalidateQueries({
        queryKey: ["getInfiniteUserGroups"] // Invalidate getInfiniteUserGroups
      });
      queryClient.invalidateQueries({
        queryKey: ["getUsers"] // Invalidate all users queries
      });
    },
    [queryClient]
  );

  const { mutate: updateGroupData, isPending: isUpdatingGroup } =
    usePutUserGroupAPI({
      onSuccess: (response) => {
        invalidateQueryAfterAction(response);
        showGroupManagementToast({
          type: EToastType.SUCCESS,
          operation: "edit",
          status: "success",
          id: "update-user-group-success"
        });
        setGroupPanelMode(null);
      },
      onError: () => {
        showGroupManagementToast({
          type: EToastType.ERROR,
          operation: "edit",
          status: "generalError",
          id: "update-user-group-error"
        });
      }
    });

  const { mutate: createGroupData, isPending: isCreatingGroup } =
    usePostUserGroupAPI({
      onSuccess: () => {
        invalidateQueryAfterAction();
        showGroupManagementToast({
          type: EToastType.SUCCESS,
          operation: "create",
          status: "success",
          id: "create-user-group-success"
        });
        setGroupPanelMode(null);
      },
      onError: (error, payload) => {
        const status = error.code === 409 ? "duplicateError" : "generalError";
        showGroupManagementToast({
          type: EToastType.ERROR,
          operation: "create",
          status,
          id: "create-user-group-error",
          additionalMessage: payload.name as string
        });
      }
    });

  const { mutateAsync: deleteUserGroup } = useDeleteUserGroupAPI({
    onSuccess: () => {
      invalidateQueryAfterAction();
      showGroupManagementToast({
        type: EToastType.SUCCESS,
        operation: "delete",
        status: "success",
        id: "delete-user-group-success",
        additionalMessage: selectedGroup?.name
      });
      setGroupPanelMode(null);
    },
    onError: () => {
      showGroupManagementToast({
        type: EToastType.ERROR,
        operation: "delete",
        status: "generalError",
        id: "delete-user-group-error"
      });
    }
  });

  const userPermissions = useGetUserPermission();
  const isCreateGroupGranted =
    userPermissions[EModuleName.USER_GROUP_MANAGEMENT][
      EModulePermission.CREATE
    ];
  const isEditGroupGranted =
    userPermissions[EModuleName.USER_GROUP_MANAGEMENT][EModulePermission.EDIT];
  const isDeleteGroupGranted =
    userPermissions[EModuleName.USER_GROUP_MANAGEMENT][
      EModulePermission.DELETE
    ];

  const handleShowViewGroupPanel = (data: GetUserGroupsData) => {
    setSelectedGroup(data);
    setGroupPanelMode("view");
  };

  const handleShowEditGroupPanel = (data: GetUserGroupsData) => {
    setSelectedGroup(data);
    setGroupPanelMode("edit");
  };

  const handleShowAddGroupPanel = () => {
    setGroupPanelMode("add");
  };

  const handleDeleteGroup = async (data: DeleteGroupPayload) => {
    showConfirmation({
      title: MESSAGE.DELETE_GROUP_MANAGEMENT_TITLE_SUCCESS_CONFIRMATION,
      message: (
        <div className="text-center">
          {MESSAGE.DELETE_GROUP_MANAGEMENT_DESCRIPTION_CONFIRMATION_PREFIX}
          <span className="font-semibold mx-0.5">{data?.name ?? ""}</span>
          {MESSAGE.DELETE_GROUP_MANAGEMENT_DESCRIPTION_CONFIRMATION_SUFIX}
        </div>
      ),
      cancelLabel: "Cancel",
      confirmLabel: "Delete",
      onConfirm: async () => {
        try {
          await deleteUserGroup({
            id: data.id
          });
        } catch {
          return; // catch block only returns here because the error will be handled by the onError above
        }
      }
    });
  };

  const handleAddSubmit = (data: AddGroupFormData) => {
    const payload = {
      name: data.name,
      description: data.description,
      status: data.status,
      group_permission: data.group_permission?.map((permission) => ({
        feature_id: permission.feature_id,
        permission: permission.selectedPermission?.value ?? ""
      }))
    };
    createGroupData(payload);
  };

  const handleEditSubmit = (data: EditGroupFormData) => {
    const payload = {
      id: data.id,
      name: data.name,
      description: data.description,
      status: data.status,
      group_permission: data.group_permission?.map((permission) => ({
        feature_id: permission.feature_id,
        permission: permission.selectedPermission?.value ?? ""
      }))
    };
    updateGroupData(payload);
  };

  // Check if the data on the db / server is empty
  // It is marked by total number of users is 0 when page is 1 with default search query
  // This is used to determine if we should show "No Data" or "No Results" message
  const isDataOnServerEmpty = useMemo(
    () =>
      userGroupsData?.meta?.pagination?.total === 0 &&
      searchParams.page === 1 &&
      !searchParams.query,
    [
      searchParams.page,
      searchParams.query,
      userGroupsData?.meta?.pagination?.total
    ]
  );

  const messageMapping = isDataOnServerEmpty ? "no-data" : "no-result";

  return (
    <div className="flex flex-col gap-2 h-full overflow-auto py-3 px-6">
      <GroupManagementHeader
        title="Groups"
        createButtonText="Create Group"
        onCreateClick={() => {
          handleShowAddGroupPanel();
        }}
        breadcrumbConfig={GROUP_MANAGEMENT_BREADCRUMB_CONFIG}
        createPermission={isCreateGroupGranted}
      />
      <GroupManagementGroupList<GetUserGroupsData, GetGroupsPayload>
        dashboardData={userGroupsData}
        onParamsChange={(newValue) => {
          if (newValue?.query) {
            // debounce the search params if new query is provided
            debouncedSetSearchParams(newValue);
          } else {
            setSearchParams((prevValue) => ({
              ...prevValue,
              ...newValue
            }));
          }
        }}
        isLoadingData={isUserGroupsLoading}
        columns={columns({
          handleShowViewGroupPanel,
          handleShowEditGroupPanel,
          handleShowDeleteModal: (data) => {
            setSelectedGroup(data);
            handleDeleteGroup(data);
          },
          editPermission: isEditGroupGranted,
          deletePermission: isDeleteGroupGranted,
          timezone: userProfileData?.timezone
        })}
        statusOptions={GroupStatusOptions}
        emptyDataMessage={
          <NoDataMessage
            message={GROUP_MANAGEMENT_NO_DATA_MESSAGE_MAPPING[messageMapping]}
          />
        }
        testId={TEST_ID_PREFIX}
      />
      {groupPanelMode && (
        <GroupPanel
          groupPanelMode={groupPanelMode}
          setGroupPanelMode={setGroupPanelMode}
          groupData={selectedGroup}
          onDelete={(data) => handleDeleteGroup(data)}
          isSubmitting={isUpdatingGroup || isCreatingGroup}
          onClose={() => {
            if (groupPanelMode === "view") {
              setGroupPanelMode(null);
            } else {
              showConfirmation({
                title: "Unsaved Changes",
                message: MESSAGE.CONFIRMATION_LEAVE,
                cancelLabel: "Cancel",
                confirmLabel: "Leave",
                onConfirm: () => {
                  setGroupPanelMode(null);
                }
              });
            }
          }}
          onCreate={handleAddSubmit}
          onUpdate={handleEditSubmit}
        />
      )}
    </div>
  );
};

export default GroupManagementPage;
